import { Alert, Button, Group, Loader, Text } from "@mantine/core";
import { IconChevronLeft, IconChevronRight, IconChevronsLeft } from "@tabler/icons-react";
import { LivePage } from "./useLivePagedQuery";

/** Pagination bar + live indicator for useLivePagedQuery lists. */
export function CtcPager<T extends { id?: string }>({ page }: { page: LivePage<T> }) {
    const from = page.page * page.pageSize + (page.rows.length ? 1 : 0);
    const to = page.page * page.pageSize + page.rows.length;
    return (
        <Group justify="space-between" mt="sm">
            <Group gap="xs">
                {page.loading ? <Loader size="xs" /> : <span title="Updates live" style={{ width: 8, height: 8, borderRadius: 4, background: "var(--mantine-color-green-6)", display: "inline-block" }} />}
                <Text size="xs" c="dimmed">
                    {page.rows.length ? `${from}–${to}` : "0"}
                    {page.total !== undefined ? ` of ${page.total.toLocaleString()}` : ""}
                    {" · page "}{page.page + 1}
                    {page.total !== undefined ? ` of ${Math.max(1, Math.ceil(page.total / page.pageSize))}` : ""}
                </Text>
            </Group>
            <Group gap={4}>
                <Button size="compact-xs" variant="subtle" disabled={!page.hasPrev} onClick={page.first} leftSection={<IconChevronsLeft size={14} />}>First</Button>
                <Button size="compact-xs" variant="default" disabled={!page.hasPrev} onClick={page.prev} leftSection={<IconChevronLeft size={14} />}>Previous</Button>
                <Button size="compact-xs" variant="default" disabled={!page.hasNext} onClick={page.next} rightSection={<IconChevronRight size={14} />}>Next</Button>
            </Group>
        </Group>
    );
}

/** Firestore errors worth showing (missing index while indexes deploy, permission problems). */
export function CtcQueryError({ error }: { error?: { code?: string; message?: string } }) {
    if (!error) return null;
    const indexBuilding = error.code === "failed-precondition" && /index/i.test(error.message ?? "");
    return (
        <Alert color={indexBuilding ? "yellow" : "red"} mb="sm" title={indexBuilding ? "Filter unavailable" : "Could not load results"}>
            <Text size="sm">{indexBuilding ? "This filter is not available yet. Try another filter or contact support." : error.message}</Text>
        </Alert>
    );
}
